/**
 * Inbox-zero gamification (light): a per-account daily streak, computed
 * client-side from the folder counts the app already has — no server state,
 * no tracking. The day the INBOX unread count hits zero, the day is "achieved";
 * consecutive achieved days build a streak (kept alive through yesterday, so
 * opening the app at 09:00 does not zero an intact streak from last night).
 *
 * Pure logic lives in `recordZero`/`currentStreak` (unit-tested); persistence
 * is a tiny localStorage record per account; `useInboxZero` wires both to the
 * reactive folder counts.
 */

import { createEffect, createSignal } from "solid-js";

import type { MailFolder } from "./api";

// The IMAP inbox — a protocol name (RFC 3501), not UI copy.
const INBOX = "INBOX";

export interface InboxZeroState {
  /** Local day (`YYYY-MM-DD`) the inbox was last seen at zero unread. */
  lastZeroDay: string | null;
  /** Consecutive achieved days ending at `lastZeroDay`. */
  streak: number;
  /** Best streak ever reached. */
  best: number;
  /** Lifetime count of achieved days. */
  totalZeroDays: number;
}

export const INITIAL_INBOX_ZERO: InboxZeroState = {
  lastZeroDay: null,
  streak: 0,
  best: 0,
  totalZeroDays: 0,
};

/** Local-timezone day key (`YYYY-MM-DD`) — inbox zero is a "day" for the user. */
export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** True when `next` is exactly the day after `prev` (both `YYYY-MM-DD`). */
export function isNextDay(prev: string, next: string): boolean {
  const [y, m, d] = prev.split("-").map(Number);
  if (!y || !m || d === undefined) {
    return false;
  }
  // Noon avoids any DST edge shifting the derived date across midnight.
  const after = new Date(y, m - 1, d + 1, 12);
  return dayKey(after) === next;
}

/**
 * Mark `today` as achieved. Idempotent for the same day; a gap since the last
 * achieved day restarts the streak at 1.
 */
export function recordZero(state: InboxZeroState, today: string): InboxZeroState {
  if (state.lastZeroDay === today) {
    return state;
  }
  const streak = state.lastZeroDay && isNextDay(state.lastZeroDay, today) ? state.streak + 1 : 1;
  return {
    lastZeroDay: today,
    streak,
    best: Math.max(state.best, streak),
    totalZeroDays: state.totalZeroDays + 1,
  };
}

/**
 * The streak as of `today`: alive when the last achieved day is today or
 * yesterday (you have until tonight to keep it), otherwise 0.
 */
export function currentStreak(state: InboxZeroState, today: string): number {
  if (!state.lastZeroDay) {
    return 0;
  }
  if (state.lastZeroDay === today || isNextDay(state.lastZeroDay, today)) {
    return state.streak;
  }
  return 0;
}

// ─── Persistence (per account, localStorage) ────────────────────────────────

const storageKey = (accountId: string) => `cartero.inbox-zero.${accountId}`;

export function loadInboxZero(accountId: string): InboxZeroState {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey(accountId));
    if (!raw) {
      return INITIAL_INBOX_ZERO;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return INITIAL_INBOX_ZERO;
    }
    const p = parsed as Partial<InboxZeroState>;
    return {
      lastZeroDay: typeof p.lastZeroDay === "string" ? p.lastZeroDay : null,
      streak: typeof p.streak === "number" ? p.streak : 0,
      best: typeof p.best === "number" ? p.best : 0,
      totalZeroDays: typeof p.totalZeroDays === "number" ? p.totalZeroDays : 0,
    };
  } catch {
    return INITIAL_INBOX_ZERO;
  }
}

export function saveInboxZero(accountId: string, state: InboxZeroState): void {
  try {
    globalThis.localStorage?.setItem(storageKey(accountId), JSON.stringify(state));
  } catch {
    // Storage full/unavailable — the streak is a nicety, never an error.
  }
}

// ─── Reactive wiring ────────────────────────────────────────────────────────

/**
 * Watches the account's folder counts; whenever the INBOX shows zero unread it
 * records today as achieved. Returns the live streak (0 hides the badge).
 */
export function useInboxZero(
  accountId: () => string | undefined,
  folders: () => MailFolder[] | undefined,
): { streak: () => number; best: () => number } {
  const [state, setState] = createSignal<InboxZeroState>(INITIAL_INBOX_ZERO);

  createEffect(() => {
    const id = accountId();
    const fs = folders();
    if (!id) {
      setState(INITIAL_INBOX_ZERO);
      return;
    }
    let next = loadInboxZero(id);
    const inbox = fs?.find((f) => f.name.toUpperCase() === INBOX);
    if (inbox && inbox.unread === 0) {
      const achieved = recordZero(next, dayKey(new Date()));
      if (achieved !== next) {
        saveInboxZero(id, achieved);
        next = achieved;
      }
    }
    setState(next);
  });

  return {
    streak: () => currentStreak(state(), dayKey(new Date())),
    best: () => state().best,
  };
}
